// User Permissions — Wave 7 Day 1 (Priority 8).
//
// Lives in lib/ (not mocks/) because real-API screens still read the canonical
// persona capabilities (header org switcher, supply print-queue gate). The
// fictional directory-density users stay in mocks/data/user-permissions.ts so
// the production bundle does not carry the MSW fixture set (OPT-20261031-05).
//
// V1 simulation of A-32 5-tier RBAC EXTENSION + capability gates. Real backend
// resolves from JWT membership claims (Faz 1 memberships table); V1 mock layer
// for "Şu an hangi yetkilerle login'im?" visibility UX.
//
// Per memory feedback_pricing_tier_count_v1.md: tier ≠ org_type (orthogonal).
// All karakter set users are 'owner' tier of their own org; capability diff
// driven by org_type (BO2 seller capabilities ≠ SO2 partner capabilities).
//
// V1.5+ unlock: granular per-user tier (Member/Designer/CustomerService),
// capability_overrides JSONB, scope_family bounded access.

// T1.10 shipping-v1 2026-06-05: warehouse_operator tier added per A-32 §A7
// (V2 amendment). NJ depot operator (Emin persona) — scan/dispatch focus.
export type RoleTier =
  | 'owner'
  | 'admin'
  | 'designer'
  | 'member'
  | 'customer_service'
  | 'warehouse_operator'

// T1.10 shipping-v1 2026-06-05: shipping category added per A-32 §A7.
export type CapabilityCategory =
  | 'catalog'
  | 'orders'
  | 'pricing'
  | 'drive'
  | 'production'
  | 'team'
  | 'settings'
  | 'finance'
  | 'platform'
  | 'shipping'

export type Capability = {
  id: string
  category: CapabilityCategory
  label: string
  description: string
}

export const ALL_CAPABILITIES: Capability[] = [
  // catalog
  { id: 'catalog.view', category: 'catalog', label: 'View catalog', description: 'View master catalog products' },
  { id: 'catalog.edit', category: 'catalog', label: 'Edit catalog', description: 'Add / update / archive products' },
  { id: 'catalog.activate', category: 'catalog', label: 'Activate products', description: 'Draft → active transition' },
  // orders
  { id: 'orders.view', category: 'orders', label: 'View orders', description: 'View orders belonging to your own org' },
  { id: 'orders.update', category: 'orders', label: 'Update orders', description: 'State transition + tag + assign' },
  { id: 'orders.bulk', category: 'orders', label: 'Bulk operations', description: 'Multi-order operations (label / cancel)' },
  { id: 'orders.label_purchase', category: 'orders', label: 'Purchase shipping label', description: 'B-4 §G shipping label resale' },
  // pricing
  { id: 'pricing.view', category: 'pricing', label: 'View pricing', description: 'Tier prices + multiplier' },
  { id: 'pricing.edit', category: 'pricing', label: 'Edit pricing', description: 'Per-product multiplier override' },
  { id: 'pricing.hub_admin', category: 'pricing', label: 'Pricing Hub admin', description: 'Global multipliers + cost-range rules' },
  // drive
  { id: 'drive.view', category: 'drive', label: 'View Drive', description: 'View assets shared with your org' },
  { id: 'drive.upload', category: 'drive', label: 'Upload to Drive', description: 'Asset upload + role pick' },
  { id: 'drive.share', category: 'drive', label: 'Share Drive', description: 'Cross-org ACL grant (D-5 Ring 2)' },
  { id: 'drive.audit_admin', category: 'drive', label: 'Drive Audit admin', description: 'Cross-product Ring 3 view' },
  // production
  { id: 'wo.view', category: 'production', label: 'View Work Orders', description: 'View assigned work orders' },
  { id: 'wo.transition', category: 'production', label: 'WO state transition', description: 'assigned → shipped flow' },
  { id: 'wo.upload_photo', category: 'production', label: 'Upload production photo', description: 'WO-scoped drive asset upload' },
  { id: 'wo.packaging.advance', category: 'production', label: 'Advance packaging stage', description: 'NJ depot WO packaging state transition (T1.10 warehouse_operator default)' },
  // team
  { id: 'team.view', category: 'team', label: 'View team', description: 'Member list + roles' },
  { id: 'team.edit', category: 'team', label: 'Edit team', description: 'Invite / remove / change role' },
  // settings
  { id: 'settings.view', category: 'settings', label: 'View settings', description: 'Org settings' },
  { id: 'settings.edit', category: 'settings', label: 'Edit settings', description: 'Profile, notifications, brand' },
  // finance
  { id: 'finance.view_costs', category: 'finance', label: 'View costs', description: 'Cost visibility (cost masking bypass)' },
  { id: 'finance.wallet_admin', category: 'finance', label: 'Wallet admin', description: 'Settlement runs + payouts' },
  // platform
  { id: 'platform.user_admin', category: 'platform', label: 'User admin', description: 'A1 Ring 3 governance' },
  { id: 'platform.audit_view', category: 'platform', label: 'View audit log', description: 'Cross-org activity feed' },
  { id: 'platform.access_seller_orgs', category: 'platform', label: 'Seller org access', description: 'D-5 Ring 3 — enter any seller org dashboard with platform authority (read-only)' },
  { id: 'platform.access_vendor_orgs', category: 'platform', label: 'Vendor org access', description: 'D-5 Ring 3 — enter any vendor org dashboard with platform authority (read-only)' },
  // T1.10 shipping-v1: inventory.physical.adjust (NJ depot receive/dispatch — warehouse_operator default)
  { id: 'inventory.physical.adjust', category: 'platform', label: 'Adjust physical inventory', description: 'NJ depot receive/dispatch operations (T1.10 warehouse_operator default)' },
  // ── shipping (T1.10 A-32 §A7 — 5 capabilities)
  { id: 'ship.rate_quote.read', category: 'shipping', label: 'View shipping rate quote', description: 'Carrier rate quotation (admin all + seller own + supply own WO)' },
  { id: 'ship.label.purchase', category: 'shipping', label: 'Purchase shipping label', description: 'Carrier API label purchase (admin all + seller after approval gate)' },
  { id: 'ship.label.void', category: 'shipping', label: 'Void shipping label', description: 'D-5 Ring 3 — admin only (seller request only)' },
  { id: 'ship.tracking.read', category: 'shipping', label: 'View tracking', description: 'Shipment tracking (admin all + seller own + supply own WO + Etsy push)' },
  { id: 'ship.shipment.scan', category: 'shipping', label: 'Scan shipment barcode', description: 'NJ depot scan/dispatch (admin + warehouse_operator only)' },
]

export const CATEGORY_LABELS: Record<CapabilityCategory, string> = {
  catalog: 'Catalog',
  orders: 'Orders',
  pricing: 'Pricing',
  drive: 'Drive',
  production: 'Production',
  team: 'Team',
  settings: 'Settings',
  finance: 'Finance',
  platform: 'Platform (A1)',
  shipping: 'Shipping',
}

export const TIER_LABELS: Record<RoleTier, string> = {
  owner: 'Owner',
  admin: 'Admin',
  designer: 'Designer',
  member: 'Member',
  customer_service: 'Customer Service',
  warehouse_operator: 'Warehouse Operator',
}

export type UserStatus = 'active' | 'pending' | 'suspended'
export type LastSeenStatus = 'active' | 'recent' | 'idle'

export type UserPermission = {
  user_id: string
  user_label: string
  org_id: string
  org_name: string
  org_type: 'A1' | 'A2' | 'BO1' | 'BO2' | 'BO3' | 'SO1' | 'SO2' | 'SO3'
  tier: RoleTier
  effective_capabilities: string[] // Capability.id list
  granted_at: string
  // ─── Admin directory UI enrichment (optional — V1 simulation) ───
  email?: string
  country_code?: string // 2-letter ISO
  city?: string
  last_seen_label?: string
  last_seen_status?: LastSeenStatus
  status?: UserStatus
  risk_flag?: boolean
  org_role_label?: string
  initials?: string
}

// V1 seed: 5 karakter (Volkan/Sarah/Hans/Mehmet/Aybars) + Hakan supply persona
// All 'owner' tier per their own org (V1 simplification; V1.5+ multi-tier)
//
// UI enrichment (email/country/lastSeen/status) added Wave 7 Day 3 (admin
// directory redesign) — backend fields are V1 simulation hints; V1.5+
// resolved from JWT membership + audit_events table.
export const USER_PERMISSIONS: UserPermission[] = [
  {
    user_id: 'usr_volkan_a1',
    user_label: 'Volkan Bilezikçi',
    org_id: 'org_volkan_admin',
    org_name: 'PEXIFY Platform',
    org_type: 'A1',
    tier: 'owner',
    effective_capabilities: ALL_CAPABILITIES.map((c) => c.id), // Ring 3 bypass — all
    granted_at: '2026-04-01T08:00:00Z',
    email: 'volkan@pexify.com',
    country_code: 'US',
    city: 'NYC',
    last_seen_label: 'şu an aktif',
    last_seen_status: 'active',
    status: 'active',
    org_role_label: 'Platform Owner',
    initials: 'VB',
  },
  {
    user_id: 'usr_sarah_bo2',
    user_label: 'Sarah Johnson',
    org_id: 'org_sarah_thoughtful',
    org_name: 'Thoughtful Gifts Co.',
    org_type: 'BO2',
    tier: 'owner',
    effective_capabilities: [
      'catalog.view',
      'orders.view',
      'orders.update',
      'orders.bulk',
      'pricing.view',
      'drive.view',
      'drive.upload',
      'team.view',
      'team.edit',
      'settings.view',
      'settings.edit',
      // T1.10 shipping-v1 §A7: seller-own rate quote + label purchase (post-approval gate) + tracking
      'ship.rate_quote.read',
      'ship.label.purchase',
      'ship.tracking.read',
    ],
    granted_at: '2026-04-10T12:00:00Z',
    email: 'sarah@thoughtfulgifts.co',
    country_code: 'US',
    city: 'Austin TX',
    last_seen_label: 'şu an aktif',
    last_seen_status: 'active',
    status: 'active',
    org_role_label: 'Reseller',
    initials: 'SJ',
  },
  {
    user_id: 'usr_hans_bo3',
    user_label: 'Hans Müller',
    org_id: 'org_hans_germany',
    org_name: 'Müller Wholesale GmbH',
    org_type: 'BO3',
    tier: 'owner',
    effective_capabilities: [
      'catalog.view',
      'orders.view',
      'pricing.view',
      'drive.view',
      'team.view',
      'settings.view',
      'settings.edit',
      // T1.10 shipping-v1 §A7: BO3 tracking only (read-only, B2B)
      'ship.tracking.read',
    ],
    granted_at: '2026-04-15T09:00:00Z',
    email: 'hans@muller-wholesale.de',
    country_code: 'DE',
    city: 'Hamburg',
    last_seen_label: '4 gün önce',
    last_seen_status: 'idle',
    status: 'active',
    org_role_label: 'B2B Wholesale',
    initials: 'HM',
  },
  {
    user_id: 'usr_mehmet_so2',
    user_label: 'Mehmet Üretim Ortağı',
    org_id: 'org_mehmet_workshop',
    org_name: 'Mehmet Atölye (Bursa)',
    org_type: 'SO2',
    tier: 'owner',
    effective_capabilities: [
      'wo.view',
      'wo.transition',
      'wo.upload_photo',
      'drive.view',
      'drive.upload',
      'team.view',
      'settings.view',
      'settings.edit',
      // T1.10 shipping-v1 §A7: supply-own WO rate quote + tracking (no label purchase/void/scan)
      'ship.rate_quote.read',
      'ship.tracking.read',
    ],
    granted_at: '2026-04-15T10:00:00Z',
    email: 'mehmet@bursaatolye.com.tr',
    country_code: 'TR',
    city: 'Bursa',
    last_seen_label: '25 dk önce',
    last_seen_status: 'active',
    status: 'active',
    org_role_label: 'Production Partner',
    initials: 'MÜ',
  },
  {
    user_id: 'usr_hakan_so2',
    user_label: 'Hakan SO2 Bursa Partner',
    org_id: 'org_hakan_factory',
    org_name: 'Hakan Fabrika (İstanbul)',
    org_type: 'SO2',
    tier: 'owner',
    effective_capabilities: [
      'wo.view',
      'wo.transition',
      'wo.upload_photo',
      'drive.view',
      'drive.upload',
      'team.view',
      'settings.view',
      'settings.edit',
      // T1.10 shipping-v1 §A7: supply-own WO rate quote + tracking
      'ship.rate_quote.read',
      'ship.tracking.read',
    ],
    granted_at: '2026-04-20T14:00:00Z',
    email: 'hakan@istanbulfabrika.com.tr',
    country_code: 'TR',
    city: 'İstanbul',
    last_seen_label: '1 saat önce',
    last_seen_status: 'recent',
    status: 'active',
    org_role_label: 'Production Partner',
    initials: 'HS',
  },
  {
    user_id: 'usr_aybars_so3',
    user_label: 'Aybars Toptan Tedarik',
    org_id: 'org_aybars_vendor',
    org_name: 'Aybars Toptan',
    org_type: 'SO3',
    tier: 'owner',
    effective_capabilities: [
      'catalog.view',
      'drive.view',
      'drive.upload',
      'team.view',
      'settings.view',
      'settings.edit',
    ],
    granted_at: '2026-05-01T09:00:00Z',
    email: 'aybars@izmirtoptan.com.tr',
    country_code: 'TR',
    city: 'İzmir',
    last_seen_label: 'dün',
    last_seen_status: 'idle',
    status: 'active',
    org_role_label: 'Vendor',
    initials: 'AT',
  },
  // T1.10 shipping-v1 2026-06-05: Emin NJ depot warehouse_operator (A-32 §A7)
  // A2 internal staff (PEXIFY-owned NJ depot); persona rename Aybars→Emin
  // 2026-06-04 per reference_persona_rename_aybars_to_emin_2026_06_04 — Emin
  // is the new NJ depot operator persona for shipping/scan operations.
  // V1 IAM mock; V1.5+ JWT membership backend resume.
  {
    user_id: 'usr_emin_a2',
    user_label: 'Emin (NJ Depot)',
    org_id: 'org_volkan_admin', // A2 internal staff at PEXIFY platform org
    org_name: 'PEXIFY NJ Warehouse',
    org_type: 'A2',
    tier: 'warehouse_operator',
    effective_capabilities: [
      // warehouse_operator defaults per A-32 §A7
      'ship.shipment.scan',
      'ship.tracking.read',
      'inventory.physical.adjust',
      'wo.packaging.advance',
      // Operational baseline (read-only context for NJ depot work)
      'wo.view',
      'orders.view',
      'drive.view',
      'team.view',
      'settings.view',
    ],
    granted_at: '2026-05-15T08:00:00Z',
    email: 'emin@pexify.com',
    country_code: 'US',
    city: 'NJ',
    last_seen_label: '5 dk önce',
    last_seen_status: 'active',
    status: 'active',
    org_role_label: 'NJ Warehouse Operator',
    initials: 'EM',
  },
]

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function findUserPermission(userId: string): UserPermission | undefined {
  return USER_PERMISSIONS.find((u) => u.user_id === userId)
}

/**
 * Resolve current user by Role context (mock role switcher → user mapping).
 * V1 simulation; V1.5+ JWT claims drive this.
 */
export function resolveCurrentUser(args: {
  role: 'admin' | 'seller' | 'supply'
  supplyPersona?: string // 'so2_mehmet' | 'so2_hakan' | 'so3_aybars' | 'so2_aybars' | 'wh_emin'
}): UserPermission {
  if (args.role === 'admin') return findUserPermission('usr_volkan_a1')!
  if (args.role === 'seller') return findUserPermission('usr_sarah_bo2')!
  // supply: persona drives
  const personaMap: Record<string, string> = {
    so2_mehmet: 'usr_mehmet_so2',
    so2_hakan: 'usr_hakan_so2',
    so3_aybars: 'usr_aybars_so3',
    // T1.10 shipping-v1: Emin NJ depot warehouse_operator (A-32 §A7)
    wh_emin: 'usr_emin_a2',
  }
  const userId = personaMap[args.supplyPersona ?? 'so3_aybars'] ?? 'usr_aybars_so3'
  return findUserPermission(userId)!
}

export function listAllUsers(): UserPermission[] {
  return USER_PERMISSIONS.slice()
}

export function hasCapability(userId: string, capabilityId: string): boolean {
  const user = findUserPermission(userId)
  if (!user) return false
  return user.effective_capabilities.includes(capabilityId)
}
